import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, type DeliverableLink } from '../lib/api'
import { Modal } from './ui/Modal'

interface DeliverablesModalProps {
  open: boolean
  briefId: string
  onClose: () => void
}

export function DeliverablesModal({ open, briefId, onClose }: DeliverablesModalProps) {
  const { t } = useTranslation()
  const [links, setLinks] = useState<DeliverableLink[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLinks(null)
    setError(null)
    api<DeliverableLink[]>(`/api/v1/briefs/${briefId}/deliverables`)
      .then((data) => {
        if (!cancelled) setLinks(data)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : t('errors.generic'))
      })
    return () => {
      cancelled = true
    }
  }, [open, briefId, t])

  return (
    <Modal open={open} title={t('brief_detail.deliverables.title')} onClose={onClose}>
      <p className="mb-4 text-sm text-text-muted">
        {t('brief_detail.deliverables.subtitle')}
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!error && !links && <p className="text-sm text-text-muted">{t('common.loading')}</p>}
      {links && (
        <ul className="space-y-2">
          {links.map((d) => (
            <li
              key={`${d.audience}-${d.format}`}
              className="flex items-center justify-between rounded-md border border-warm-mist px-3 py-2"
            >
              <div>
                <p className="text-sm font-medium text-ink">{d.filename}</p>
                <p className="text-xs text-text-muted">
                  {d.audience} · {d.format}
                </p>
              </div>
              <a
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-matrix-green px-3 py-1.5 text-xs font-medium text-ink hover:bg-matrix-glow"
              >
                {t('common.download')}
              </a>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
